import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Filter, Clock, PlayCircle, ArrowUpRight, Mic, Star, Headphones, Share2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatPublishDate, resolveMediaUrl } from "@/lib/podcast";
import { useCms } from "@/lib/cms";
import PodcastBookingModal from "@/components/PodcastBookingModal";

const SESSION_KEY = "podcast_booking_modal_shown";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] },
  }),
};

const PAGE_SIZE = 9;

// Not a stored field — the Podcast model has no episode_number. Computed honestly from
// each post's position in the real, already-sorted (newest-first) list, purely a display
// convenience, not a claim that this is persisted metadata.
const episodeNumber = (allPosts, post, total) => {
  const idx = allPosts.findIndex((p) => p.id === post.id);
  return idx === -1 ? null : total - idx;
};

const share = async (url, title) => {
  if (navigator.share) {
    try { await navigator.share({ title, url }); } catch { /* user cancelled */ }
  } else {
    await navigator.clipboard?.writeText(url);
    toast.success("Link copied");
  }
};

export default function Podcast() {
  const { site } = useCms();
  const brandName = site?.company_name || "";
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [bookingOpen, setBookingOpen] = useState(false);

  // Auto-open once per session — after 8-10s OR 50% scroll, whichever comes first. Neither
  // trigger fires again once sessionStorage records the modal has already shown; the
  // "Book Free Podcast" button always reopens it manually regardless of this flag.
  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return;
    const markShown = () => sessionStorage.setItem(SESSION_KEY, "1");

    const timer = setTimeout(() => {
      if (!sessionStorage.getItem(SESSION_KEY)) {
        setBookingOpen(true);
        markShown();
      }
    }, 9000);

    const onScroll = () => {
      const scrolled = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && scrolled / max >= 0.5 && !sessionStorage.getItem(SESSION_KEY)) {
        setBookingOpen(true);
        markShown();
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    api.get("/podcast/categories").then((r) => setCategories(r.data || [])).catch(() => setCategories([]));
  }, []);

  const load = useCallback((skip = 0, append = false) => {
    append ? setLoadingMore(true) : setLoading(true);
    const params = { limit: PAGE_SIZE, skip };
    if (cat !== "All") params.category = cat;
    if (q) params.q = q;
    api
      .get("/podcast/posts", { params })
      .then((r) => {
        setPosts((prev) => (append ? [...prev, ...(r.data.posts || [])] : r.data.posts || []));
        setTotal(r.data.total || 0);
      })
      .catch(() => !append && setPosts([]))
      .finally(() => (append ? setLoadingMore(false) : setLoading(false)));
  }, [cat, q]);

  useEffect(() => {
    load(0, false);
  }, [load]);

  const featured = useMemo(() => posts.find((p) => p.featured) || posts[0], [posts]);
  const rest = useMemo(() => posts.filter((p) => p.id !== featured?.id), [posts, featured]);

  return (
    <div data-testid="podcast-page" style={{ backgroundColor: "#F8FAFC" }}>
      {/* HERO — editorial, matching the homepage's light language */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(59,104,231,0.08),transparent_50%),radial-gradient(ellipse_at_bottom_right,rgba(69,184,172,0.08),transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-20 sm:pt-28 pb-16">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <motion.div
                variants={fadeUp} initial="hidden" animate="show" custom={0}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-6"
                style={{ backgroundColor: "#3B68E714", color: "#3B68E7" }}
              >
                <Mic className="w-3.5 h-3.5" aria-hidden="true" /> Stories, episodes &amp; articles
              </motion.div>
              <motion.h1
                variants={fadeUp} initial="hidden" animate="show" custom={1}
                className="rebrand-serif tracking-tight font-bold leading-[1.05]"
                style={{ fontSize: "clamp(2.5rem, 5vw + 1rem, 4rem)", color: "#1A202C" }}
                data-testid="podcast-headline"
              >
                Ideas from the <span style={{ color: "#3B68E7" }}>classroom</span> and beyond.
              </motion.h1>
              <motion.p
                variants={fadeUp} initial="hidden" animate="show" custom={2}
                className="mt-6 text-base sm:text-lg leading-relaxed max-w-xl"
                style={{ color: "#64748B" }}
              >
                Articles, episodes, and conversations from the {brandName} team — on teaching craft, training design, and
                what actually works in classrooms.
              </motion.p>
              <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-8 flex flex-wrap gap-6">
                <div>
                  <div className="rebrand-serif text-2xl font-bold" style={{ color: "#1A202C" }}>{total || "—"}</div>
                  <div className="text-xs" style={{ color: "#64748B" }}>Episodes &amp; articles</div>
                </div>
                <div>
                  <div className="rebrand-serif text-2xl font-bold" style={{ color: "#1A202C" }}>{categories.length || "—"}</div>
                  <div className="text-xs" style={{ color: "#64748B" }}>Topics covered</div>
                </div>
              </motion.div>
              <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4} className="mt-6">
                <button
                  onClick={() => setBookingOpen(true)}
                  data-testid="podcast-book-cta"
                  className="inline-flex items-center gap-2 text-white text-sm font-semibold transition-transform hover:scale-[1.02]"
                  style={{ backgroundColor: "#3B68E7", borderRadius: 50, padding: "12px 24px", boxShadow: "0 4px 12px rgba(59,104,231,0.3)" }}
                >
                  <Mic className="w-4 h-4" aria-hidden="true" /> Book Free Podcast
                </button>
              </motion.div>
            </div>

            {/* Floating stat card, matching the homepage hero's glass badge language */}
            {featured && (
              <motion.div
                variants={fadeUp} initial="hidden" animate="show" custom={4}
                className="relative hidden lg:block"
              >
                <div className="absolute w-56 h-56 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(59,104,231,0.10) 0%, transparent 70%)", top: -30, right: -20 }} />
                <div className="relative aspect-[4/3] overflow-hidden" style={{ borderRadius: 24, border: "1px solid #E2E8F0", boxShadow: "0 20px 45px rgba(0,0,0,0.08)" }}>
                  <img src={resolveMediaUrl(featured.featured_image)} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  <div
                    className="absolute bottom-4 left-4 right-4 backdrop-blur bg-white/90 p-4"
                    style={{ borderRadius: 16 }}
                  >
                    <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: "#3B68E7" }}>Now featured</div>
                    <div className="text-sm font-semibold line-clamp-1" style={{ color: "#1A202C" }}>{featured.title}</div>
                  </div>
                  {featured.audio_url && (
                    <div className="absolute top-4 right-4 w-11 h-11 rounded-full grid place-items-center text-white" style={{ backgroundColor: "#3B68E7" }}>
                      <Headphones className="w-4.5 h-4.5" aria-hidden="true" />
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Search + category filters */}
      <section className="sticky top-0 z-20 bg-white/85 backdrop-blur-xl" style={{ borderBottom: "1px solid #E2E8F0" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "#94A3B8" }} aria-hidden="true" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search articles, episodes…"
              data-testid="podcast-search"
              className="w-full pl-10 pr-3.5 py-2.5 outline-none text-sm bg-white transition-all"
              style={{ borderRadius: 50, border: "1px solid #E2E8F0" }}
              onFocus={(e) => { e.target.style.borderColor = "#3B68E7"; e.target.style.boxShadow = "0 0 0 4px #3B68E71A"; }}
              onBlur={(e) => { e.target.style.borderColor = "#E2E8F0"; e.target.style.boxShadow = "none"; }}
            />
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-xs font-medium mr-1" style={{ color: "#64748B" }}>
            <Filter className="w-3.5 h-3.5" aria-hidden="true" /> Category
          </div>
          <div className="flex flex-wrap gap-2 overflow-x-auto no-scrollbar">
            {["All", ...categories.map((c) => c.name)].map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`podcast-cat-${c}`}
                className="px-3.5 py-1.5 text-xs font-medium whitespace-nowrap transition-all"
                style={{
                  borderRadius: 50,
                  backgroundColor: cat === c ? "#1A202C" : "#F1F5F9",
                  color: cat === c ? "#FFFFFF" : "#475569",
                }}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="ml-auto hidden sm:block text-xs font-medium" style={{ color: "#64748B" }}>
            {total} result{total === 1 ? "" : "s"}
          </div>
        </div>
      </section>

      {/* Featured post */}
      {!loading && featured && (
        <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-12">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
            <Link
              to={`/podcast/${featured.slug}`}
              data-testid={`podcast-featured-${featured.slug}`}
              className="group grid lg:grid-cols-2 gap-0 overflow-hidden bg-white transition-shadow duration-300 hover:shadow-[0_20px_45px_rgba(0,0,0,0.08)]"
              style={{ borderRadius: 24, border: "1px solid #E2E8F0", boxShadow: "0 10px 25px rgba(0,0,0,0.05)" }}
            >
              <div className="aspect-[16/10] lg:aspect-auto overflow-hidden bg-gray-100 relative">
                <img
                  src={resolveMediaUrl(featured.featured_image)} alt={featured.title} loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                />
                {(featured.embedded_videos?.length > 0 || featured.audio_url) && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-white/25 backdrop-blur flex items-center justify-center group-hover:scale-110 transition-transform">
                      <PlayCircle className="w-9 h-9 text-white" aria-hidden="true" />
                    </div>
                  </div>
                )}
              </div>
              <div className="p-8 sm:p-10 flex flex-col justify-center">
                {featured.category && (
                  <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: "#3B68E7" }}>{featured.category}</div>
                )}
                <h2 className="rebrand-serif text-2xl sm:text-3xl font-bold tracking-tight leading-snug" style={{ color: "#1A202C" }}>
                  {featured.title}
                </h2>
                <p className="mt-3 text-sm leading-relaxed line-clamp-3" style={{ color: "#64748B" }}>{featured.excerpt}</p>
                <div className="mt-6 flex items-center gap-3 text-xs" style={{ color: "#64748B" }}>
                  {featured.author && <span>{featured.author}</span>}
                  {featured.publish_date && (<><span style={{ color: "#CBD5E1" }}>·</span><span>{formatPublishDate(featured.publish_date)}</span></>)}
                  <span style={{ color: "#CBD5E1" }}>·</span>
                  <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" aria-hidden="true" /> {featured.reading_time} min read</span>
                </div>
              </div>
            </Link>
          </motion.div>
        </section>
      )}

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="overflow-hidden bg-white animate-pulse" style={{ borderRadius: 24, border: "1px solid #E2E8F0" }}>
                <div className="aspect-[16/10] bg-gray-100" />
                <div className="p-5 space-y-3">
                  <div className="h-3 w-20 bg-gray-100 rounded" />
                  <div className="h-5 w-3/4 bg-gray-100 rounded" />
                  <div className="h-3 w-full bg-gray-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : rest.length === 0 && !featured ? (
          <div className="p-12 sm:p-16 text-center" style={{ borderRadius: 24, border: "2px dashed #E2E8F0" }}>
            <Search className="w-8 h-8 mx-auto mb-3" style={{ color: "#CBD5E1" }} aria-hidden="true" />
            <div className="rebrand-serif text-xl font-semibold" style={{ color: "#1A202C" }}>Nothing published yet.</div>
            <p className="mt-2 text-sm" style={{ color: "#64748B" }}>Check back soon for new articles and episodes.</p>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((p, i) => {
                const num = episodeNumber(posts, p, total);
                const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/podcast/${p.slug}` : "";
                return (
                  <motion.div key={p.id} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }} custom={i % 6}>
                    <div
                      className="group relative overflow-hidden bg-white flex flex-col h-full transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_14px_35px_rgba(0,0,0,0.08)]"
                      style={{ borderRadius: 24, border: "1px solid #E2E8F0", boxShadow: "0 10px 25px rgba(0,0,0,0.05)" }}
                    >
                      <Link to={`/podcast/${p.slug}`} data-testid={`podcast-card-${p.slug}`} className="block">
                        <div className="aspect-[16/10] overflow-hidden bg-gray-100 relative">
                          <img
                            src={resolveMediaUrl(p.featured_image)} alt={p.title} loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                          />
                          {p.category && (
                            <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[10px] font-bold uppercase tracking-wider" style={{ color: "#3B68E7" }}>
                              {p.category}
                            </div>
                          )}
                          {p.embedded_videos?.length > 0 && (
                            <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur text-[10px] font-semibold text-white">
                              <PlayCircle className="w-3 h-3" aria-hidden="true" /> Video
                            </div>
                          )}
                          {p.featured && (
                            <div className="absolute bottom-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full backdrop-blur text-[10px] font-semibold" style={{ backgroundColor: "#DDA756F2", color: "#1A202C" }}>
                              <Star className="w-3 h-3 fill-current" aria-hidden="true" /> Featured
                            </div>
                          )}
                        </div>
                      </Link>
                      <div className="p-5 flex-1 flex flex-col">
                        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: "#94A3B8" }}>
                          {num && <span>Episode {num}</span>}
                          {p.audio_url && (<><span>·</span><span className="inline-flex items-center gap-1"><Headphones className="w-3 h-3" /> Audio</span></>)}
                        </div>
                        <Link to={`/podcast/${p.slug}`}>
                          <h3 className="rebrand-serif text-lg font-semibold leading-snug line-clamp-2" style={{ color: "#1A202C" }}>{p.title}</h3>
                        </Link>
                        {p.excerpt && <p className="mt-2 text-sm line-clamp-2 leading-relaxed" style={{ color: "#64748B" }}>{p.excerpt}</p>}
                        <div className="mt-5 pt-4 flex items-center justify-between text-xs" style={{ borderTop: "1px solid #F1F5F9" }}>
                          <span className="flex items-center gap-3" style={{ color: "#64748B" }}>
                            <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" aria-hidden="true" /> {p.reading_time} min</span>
                            {p.publish_date && (<><span style={{ color: "#CBD5E1" }}>·</span><span>{formatPublishDate(p.publish_date)}</span></>)}
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => { e.preventDefault(); share(shareUrl, p.title); }}
                              aria-label="Share"
                              data-testid={`podcast-share-${p.slug}`}
                              className="w-7 h-7 grid place-items-center rounded-full hover:bg-gray-50"
                              style={{ color: "#94A3B8" }}
                            >
                              <Share2 className="w-3.5 h-3.5" />
                            </button>
                            <Link to={`/podcast/${p.slug}`} className="font-medium inline-flex items-center gap-0.5 group-hover:gap-1.5 transition-all" style={{ color: "#3B68E7" }}>
                              Read <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {posts.length < total && (
              <div className="mt-10 text-center">
                <button
                  onClick={() => load(posts.length, true)}
                  disabled={loadingMore}
                  data-testid="podcast-load-more"
                  className="inline-flex items-center gap-2 text-sm font-medium transition-all disabled:opacity-50"
                  style={{ border: "1px solid #E2E8F0", color: "#1A202C", borderRadius: 50, padding: "12px 24px" }}
                >
                  {loadingMore ? "Loading…" : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <PodcastBookingModal open={bookingOpen} onClose={() => setBookingOpen(false)} />
    </div>
  );
}
